import React from "react";
import TopBar from "@insightfulscience/atomic-react/TopBar";
import TopBarLogo from "@insightfulscience/atomic-react/TopBar/Logo";
import TopBarNav from "@insightfulscience/atomic-react/TopBar/Nav";
import TopBarDropdown from "@insightfulscience/atomic-react/TopBar/Dropdown";
import Link from "@insightfulscience/atomic-react/Link";
import Image from "@insightfulscience/atomic-react/Image";
import MegaMenu from "@insightfulscience/atomic-react/MegaMenu";
import { MegaMenuCol, MegaMenuRow } from "@insightfulscience/atomic-react/MegaMenu/Grid";
import MegaMenuBlockLink from "@insightfulscience/atomic-react/MegaMenu/MegaMenuBlockLink";
import Block from "@insightfulscience/atomic-react/Block";
import Icon from "@insightfulscience/atomic-react/Icon";
import ButtonTheme from "@insightfulscience/atomic-react/ButtonTheme";
import { brandCodePropType } from "../propTypes";
import utmLink from "../utils/utmLink";

const logoImg = "/images/common/dotmatics-logo-white.svg";
const prismLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-Prism.svg";
const geneiousLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-Geneious.svg";
const snapgeneLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-SnapGene.svg";
const labArchivesLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-LabArchives.svg";
const proteinMetricLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-Protein-Metrics.svg";
const omiqLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-OMIQ.svg";
const fcsExpressLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-FCS-Express.svg";
const nQueryLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-nQuery.svg";
const easyPanelLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-EasyPanel.svg";
const mStarLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-M-Star.svg";
const softGeneticsLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-SoftGenetics.svg";
const virscidianLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-Virscidian.svg";
const lumaLogo = "/images/dotmatics/lab-archives/top-bar/PreNav-Logo-Luma.svg";

const Component = ({ brandCode }) => {
	const {
		gnProps,
		sfgProps,
		omProps,
		pmProps,
		laProps,
		msProps,
		nqProps,
		feProps,
		epProps,
		sgProps,
		gpProps,
		vcProps,
	} = utmLink(brandCode);

	const minHeight = 35;

	return (
		<TopBar>
			<TopBarLogo>
				<Link href="https://www.dotmatics.com/" target="_blank">
					<Image src={logoImg} />
				</Link>
			</TopBarLogo>
			<TopBarNav>
				<TopBarDropdown isMegaMenu>
					<TopBarDropdown.Title
						triangle
						triangleProps={{ color: "dotmatics-2", isOpenColor: "dotmatics-2" }}
					>
						Explore the Scientific R&D Platform
					</TopBarDropdown.Title>
					<TopBarDropdown.Body
						utility={{
							bgColor: "dotmatics-6",
							border: {
								// top: { width: 1, color: "white--20" },
								bottom: { width: 1, color: "white--20" },
							},
						}}
						spacing={{ my: 2 }}
					>
						<MegaMenu spacing={{ pt: 4, pb: 4 }}>
							<MegaMenuCol lg="shrink">
								<Block sizing={{ w: 50 }}>
									<Block spacing={{ mb: 6 }}>
										<Image src={lumaLogo} sizing={{ w: 35 }} loading="lazy" />
									</Block>
									<Block sx={{ fontSize: "m", fontColor: "white" }} spacing={{ mb: 4, pr: 4 }}>
										Scientific intelligence platform for AI-powered data management and workflow
										automation
									</Block>
									<Block>
										<ButtonTheme.Link
											href="https://www.dotmatics.com/luma"
											spacing={{ px: 5, py: 3, mt: 4, mb: 8 }}
											styleType="topbar"
											target="_blank"
										>
											Explore Luma
											<Icon spacing={{ ml: 4 }} name="link-ext" />
										</ButtonTheme.Link>
									</Block>
								</Block>
							</MegaMenuCol>
							<MegaMenuCol lg="auto">
								<MegaMenuRow>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...gpProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={prismLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Statistical analysis & graphing software for scientists
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...gnProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={geneiousLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Bioinformatics, cloning, & antibody discovery software
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...sgProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={snapgeneLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Plan, visualize, & document core molecular biology procedures
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...pmProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={proteinMetricLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Proteomics software for analysis of mass spec data
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...laProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={labArchivesLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Electronic lab notebook to organize, search & share data
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...omProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={omiqLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Modern cytometry analysis platform
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...feProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={fcsExpressLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Analysis, statistics, graphing & reporting of flow cytometry data
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...epProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={easyPanelLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Automated panel design & inventory management for flow cytometry
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...nqProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={nQueryLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Software to optimize clinical trial designs
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...msProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={mStarLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Computational fluid dynamics (CFD) software for engineers & researchers
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...sfgProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={softGeneticsLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Genetic analysis software for research, forensics & healthcare applications
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol lg={3} md={4} spacing={{ p: 1 }} sizing={{ minH: minHeight }}>
										<MegaMenuBlockLink {...vcProps}>
											<MegaMenuBlockLink.Icon />
											<MegaMenuBlockLink.Image src={virscidianLogo} loading="lazy" />
											<MegaMenuBlockLink.Description>
												Automated chromatography & mass spec software for small molecules
											</MegaMenuBlockLink.Description>
										</MegaMenuBlockLink>
									</MegaMenuCol>
									<MegaMenuCol sm={12} md={12} lg={12} spacing={{ p: 1 }}>
										<ButtonTheme.Link
											href="https://www.dotmatics.com/portfolio"
											spacing={{ px: 5, py: 3, mt: 6, mb: 4 }}
											styleType="topbar"
											target="_blank"
										>
											Explore our Software
											<Icon spacing={{ ml: 4 }} name="link-ext" />
										</ButtonTheme.Link>
									</MegaMenuCol>
								</MegaMenuRow>
							</MegaMenuCol>
						</MegaMenu>
					</TopBarDropdown.Body>
				</TopBarDropdown>
			</TopBarNav>
		</TopBar>
	);
};

Component.propTypes = {
	brandCode: brandCodePropType,
};

export default Component;
